import { Buffer } from "buffer";

import {
  createErrorResult,
  createSuccessResult,
  ERRORS,
  SIGNING_NO_KEY_PATTERN,
  SIGNING_TIMESTAMP_FLAG,
  SimpleResult,
} from "@prostocheck/shared";

import { ISignParams } from "./signing.models";

/**
 * Produces the CAdES message the fiscal server expects: the data inside
 * (`eContent`), the signer's certificate attached, no issuer certificates and no
 * revocation data. With `shouldTimestamp` the signature also carries a qualified
 * time stamp fetched from the TSA named in the certificate — that is the
 * difference between CAdES-BES and the CAdES-E-T ai/docs/prro-api.md requires.
 *
 * The private key stays in this process; only the finished message leaves.
 */
export const sign = async ({
  keyBox,
  data,
  shouldTimestamp = true,
}: ISignParams): Promise<SimpleResult<Buffer>> => {
  try {
    const signed = await keyBox.box.pipe(Buffer.from(data), [
      {
        op: "sign",
        addCert: true,
        ...(shouldTimestamp ? { tsp: SIGNING_TIMESTAMP_FLAG } : {}),
      },
    ]);

    return createSuccessResult(signed);
  } catch (signError) {
    return toSignErrorResult(signError, shouldTimestamp);
  }
};

/**
 * jkurwa rejects a failed time stamp with a bare `null`, so an error with no
 * message during a timestamped signature is a TSA problem, not a key problem.
 */
const toSignErrorResult = (
  error: unknown,
  wasTimestamped: boolean,
): SimpleResult<Buffer> => {
  const message = error instanceof Error ? error.message : "";

  if (SIGNING_NO_KEY_PATTERN.test(message)) {
    return createErrorResult(ERRORS.key.noSigningKey, message, error);
  }

  if (wasTimestamped && !message) {
    return createErrorResult(ERRORS.key.timestampFailed, undefined, error);
  }

  return createErrorResult(ERRORS.key.signingFailed, message, error);
};
